import axios from 'axios';
import UserSession from '@app/common/user-session';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export interface BillingPeriod {
  start: string;
  end: string;
}

export interface Link {
  link_id: string;
  link_name: string;
  data_used: number;
  data_limit: number;
  over_limit: boolean;
  period_start: string;
  period_end: string;
}

export interface Site {
  site_id: string;
  site_name: string;
  client_company: string;
  links: Link[];
  total_data_used: number;
  over_limit: boolean;
}

export interface ReportData {
  id: string;
  billing_period: BillingPeriod;
  sites: Site[];
  type: number;
}

export interface ReportResponse {
  request_id: string;
  status: string;
  report: ReportData;
}

// Renamed interface to indicate it's for multiple reports
export interface ReportsResponse {
  request_id: string;
  status: string;
  reports: ReportData[];
}

export interface ReportListItem {
  id: string;
  month: string;
  year: number;
  billingStart: string;
  billingEnd: string;
  downloadUrl: string; 
  size: string;
}

export interface ExportResponse {
  request_id: string;
  status: string;
  url: string;
}

export const ReportsService = {
  /**
   * Get Starlink usage reports
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @param reportType - The type of report to fetch
   * @returns Promise with all report data
   */
  getStarlinkUsageReport: async (
    startDate?: string,
    endDate?: string,
    reportType: number = 1 // Default to 1 but allow override
  ): Promise<ReportsResponse> => {
    try {
      // Get authentication token
      const token = UserSession.getToken();
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Build URL with query parameters
      let url = `${API_BASE_URL}/reports?type=${reportType}`;
      
      // Add start and end date parameters if they are provided
      if (startDate) {
        url += `&start=${startDate}`;
      }
      if (endDate) {
        url += `&end=${endDate}`;
      }
      
      // Send the request
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Return all reports from the API response
      if (response.data && response.data.reports && response.data.reports.length > 0) {
        return {
          request_id: response.data.request_id,
          status: response.data.status,
          reports: response.data.reports
        };
      }
      
      throw new Error('No reports found');
    } catch (error: any) {
      console.error('Error fetching Starlink usage reports:', error);
      throw error;
    }
  },

  /**
   * Format the report data to be displayed in the UI
   * @param reportResponse - The raw report data from the API
   * @param reportIndex - Optional index of the report to format (defaults to 0)
   * @returns Formatted report list item for UI display
   */
  formatReportForList: (reportResponse: ReportsResponse, reportIndex: number = 0): ReportListItem => {
    // Make sure there's a report at the specified index
    if (!reportResponse.reports || !reportResponse.reports[reportIndex]) {
      throw new Error('No report found at the specified index');
    }
    
    const report = reportResponse.reports[reportIndex];
    const { id } = report;
    
    // Use the billing start date directly from the API for the month and year
    // Parse the date parts to ensure correct handling of month
    const startDateStr = report.billing_period.start;
    const [year, month, day] = startDateStr.split('-').map(Number);
    
    // JavaScript months are 0-based, so we subtract 1 from the month
    const startDate = new Date(year, month - 1, day);
    
    // Calculate size based on JSON size as a placeholder
    // In a real implementation, this might come from the API
    const jsonSize = JSON.stringify(report).length;
    const sizeInMB = (jsonSize / (1024 * 1024)).toFixed(1) + ' MB';
    
    // Format month based on the billing start date 
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[startDate.getMonth()];
    
    // Create a formatted report item for the list
    return {
      id,
      month: monthName,
      year: startDate.getFullYear(),
      billingStart: report.billing_period.start,
      billingEnd: report.billing_period.end,
      downloadUrl: '#', // Placeholder for download URL
      size: sizeInMB
    };
  },

  /**
   * Generate an export of a Starlink usage report
   * @param reportId - The ID of the report to export
   * @param format - The format of the export (1 for CSV, etc.)
   * @param reportType - The type of report being exported
   * @returns Promise with the export response containing download URL
   */
  exportStarlinkUsageReport: async (
    reportId: string,
    format: number = 1,
    reportType: number = 1 // Default to 1 but allow override
  ): Promise<ExportResponse> => {
    try {
      // Get authentication token
      const token = UserSession.getToken();
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Build URL with query parameters - now using the provided report type
      const url = `${API_BASE_URL}/reports/${reportId}/exports?type=${reportType}&format=${format}`;
      
      // Send the request
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error exporting Starlink usage report:', error);
      throw error;
    }
  }
};
